import {
  Box,
  Button,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import React from 'react';
import _get from 'lodash/get';
import _classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getImageFile, replacePlaceholders } from 'utils/stringUtils';
import HeartEmptyIcon from 'components/SaveAsFavorite/images/heart-empty.svg';
import HeartFullIcon from 'components/SaveAsFavorite/images/heart-full.svg';
import { makeSelectAllowSignIn } from 'containers/Main/selectors';
import Badge from 'components/Badge';
import { useSaveToFavorite } from 'components/useSaveToFavorite';

const useStyles = makeStyles(theme => ({
  link: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: props => props.style === 'Wide Card' && '447px',
    borderRadius: 5,
    overflow: 'hidden',
    position: 'relative',
    color: theme.palette.text.white,
  },
  countBadge: {
    display: 'inline-flex',
    margin: '18px 0px 0px 23px',
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    '& span': {
      ...theme.typography.pxSmall,
      padding: '4px 8px',
      color: '#232221',
    },
  },
  mainBackground: {
    background: props =>
      ` center / cover no-repeat url(${props.backgroundImage})`,
    height: '100%',
  },
  container: {
    marginTop: 'auto',
    position: 'relative',
  },
  typeBadge: {
    color: props =>
      props.style === 'Wide Card'
        ? theme.palette.text.white
        : theme.palette.primary.main,
    height: 'auto',
    '& span': {
      color: 'inherit',
      paddingTop: 0,
    },
  },
  infoContainer: {
    position: 'relative',
    padding: '13px 20px 20px',
  },
  darkBackground: {
    position: 'absolute',
    inset: 0,
    background: `center / cover no-repeat`,
    backgroundImage: props => `url(${props.backgroundImage})`,
    filter: 'blur(4px)',
  },
  iconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  icon: {
    width: 17,
    height: 17,
  },
  description: {
    ...theme.typography.pxSmall,
  },
  truncate: {
    display: '-webkit-box',
    margin: '0 auto',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  plainCardContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    height: '100%',
    borderRadius: 5,
    padding: '12px',
    background: 'linear-gradient(79.08deg, #D2F4FD 0%, #D2FAE9 100%)',
  },
  plainCardTitle: {
    color: theme.palette.text.primary,
    fontSize: '1.1875rem',
    lineHeight: '1.625rem',
    textAlign: 'center',
  },
  plainCardDescription: {
    ...theme.typography.pxSmall,
    color: theme.palette.text.primary,
    textAlign: 'center',
  },
  imageContainer: {
    width: 80,
    height: 80,
    margin: '8px auto',
    borderRadius: 20,
    overflow: 'hidden',

    '& > img': {
      maxWidth: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  buttonLink: {
    alignSelf: 'center',
    marginTop: 8,

    '& > button': {
      borderRadius: 5,
      fontSize: '0.75rem',
      fontWeight: 700,
      lineHeight: '1.125rem',
    },
  },
}));

const allowSignInSelector = makeSelectAllowSignIn();

export const SeriesItem = ({ item, type, route, trackingPath }) => {
  const { title, description, list, style, context } = item;
  const backgroundImage = getImageFile(
    item || _get(item, 'list.fields.0.fields'),
  );
  const allowSignIn = useSelector(allowSignInSelector);
  const classes = useStyles({
    backgroundImage,
    style,
  });

  const { saved, handleSave } = useSaveToFavorite({
    item,
    type,
    trackingPath,
  });

  const favoriteIcon = allowSignIn && (
    <IconButton
      className={classes.iconButton}
      onClick={handleSave}
      disableRipple
      disableFocusRipple
    >
      <img
        src={saved ? HeartFullIcon : HeartEmptyIcon}
        alt={saved ? 'Full' : 'Empty'}
        className={classes.icon}
      />
    </IconButton>
  );

  const isWideCard = style === 'Wide Card';

  return isWideCard ? (
    <Link to={`${route}/${_get(list, 'fields.slug')}`} className={classes.link}>
      <Box className={classes.mainBackground}>
        {_get(list, 'fields.resources.length') && (
          <Box className={classes.countBadge}>
            <span>{list.fields.resources.length} Resources</span>
          </Box>
        )}
      </Box>
      <Box className={classes.container}>
        <Box className={classes.darkBackground} />
        <Box className={classes.infoContainer}>
          <Box display="flex" justifyContent="space-between">
            <Badge
              className={classes.typeBadge}
              removeMargin
              icon="seriesWhite"
              type="series"
            />
            {favoriteIcon}
          </Box>
          <Typography className={classes.truncate} variant="h5">
            {title}
          </Typography>
          <Typography
            className={_classNames(classes.description, classes.truncate)}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Link>
  ) : (
    <Box className={classes.plainCardContainer}>
      <Box display="flex" justifyContent="space-between">
        <Badge className={classes.typeBadge} removeMargin type="series" />
        {favoriteIcon}
      </Box>
      <Box className={classes.imageContainer}>
        <img src={backgroundImage} alt="Series" />
      </Box>
      <Typography
        className={_classNames(classes.plainCardTitle, classes.truncate)}
        variant="h5"
      >
        {title}
      </Typography>
      <Typography
        className={_classNames(classes.plainCardDescription, classes.truncate)}
      >
        {description}
      </Typography>
      <Link
        className={classes.buttonLink}
        to={`${route}/${_get(list, 'fields.slug')}`}
      >
        <Button variant="contained" color="primary">
          {replacePlaceholders(_get(context, 'viewResources'), {
            '#count': _get(list, 'fields.resources.length'),
          })}
        </Button>
      </Link>
    </Box>
  );
};
